import { ChartFrame } from '../components/charts/ChartFrame';
import { BarSeries } from '../components/charts/BarSeries';
import { StatTile } from '../components/tiles/StatTile';
import { IconGood, IconWarning } from '../components/Icons';
import { full, percent, relativeDays, shortDate } from '../lib/format';
import { PLATFORM_COLORS } from '../lib/platform';
import { PLATFORM_CLIENT, PLATFORM_LABELS, platformsFor } from '../data/types';
import type { Platform, PlatformFilter, Snapshot, VersionRow } from '../data/types';

interface Props {
  snap: Snapshot;
  filter: PlatformFilter;
}

function VersionList({ rows, color }: { rows: VersionRow[]; color: string }) {
  return (
    <div className="rank">
      {rows.map((v) => {
        const healthy = v.crashFreeRate >= 0.995;
        return (
          <div className="rank__row" key={v.version}>
            <span className="rank__name">
              <span className="mono" style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
                {v.version}
              </span>
              {v.isLatest ? <span className="chip">latest</span> : null}
              <span className="muted" style={{ fontSize: 11 }}>
                {relativeDays(v.releasedOn)}
              </span>
            </span>
            <span className="rank__val">
              {full(v.users)} · {percent(v.share, 0)}
            </span>
            <div className="rank__bar bar-track">
              <div className="bar-fill" style={{ width: `${v.share * 100}%`, background: color }} />
            </div>
            <span className={`sev sev--${healthy ? 'good' : 'serious'}`} style={{ gridColumn: '1 / -1' }}>
              {healthy ? <IconGood size={12} /> : <IconWarning size={12} />}
              {percent(v.crashFreeRate, 2)} crash-free
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function Releases({ snap, filter }: Props) {
  const active = platformsFor(filter);

  const byPlatform = (p: Platform) => snap.versions.filter((v) => v.platform === p);

  /** Anything past the two most recent builds is the tail you want at zero. */
  const staleShare = (p: Platform) =>
    byPlatform(p)
      .slice(2)
      .reduce((a, v) => a + v.share, 0);

  const adoptionData = active.flatMap((p) =>
    byPlatform(p).map((v) => {
      const row: Record<string, string | number> = {
        name: `${v.version} · ${PLATFORM_LABELS[p]}`,
      };
      for (const q of active) row[q] = q === p ? v.share * 100 : 0;
      return row;
    }),
  );

  return (
    <>
      <div
        className="grid"
        style={{ gridTemplateColumns: `repeat(${Math.min(5, active.length)}, minmax(0, 1fr))` }}
      >
        {active.map((p) => {
          // A live fleet does not have a build on every platform — a brand-new
          // deployment may have reported none at all. Say "no data" rather
          // than assuming the row exists.
          const latest = byPlatform(p).find((v) => v.isLatest);
          return (
            <StatTile
              key={p}
              label={`${PLATFORM_LABELS[p]} on latest`}
              value={latest ? percent(latest.share, 0) : '—'}
              sparkColor={PLATFORM_COLORS[p]}
              foot={
                latest
                  ? `v${latest.version} · ${percent(staleShare(p), 0)} on stale builds`
                  : 'No releases reported'
              }
            />
          );
        })}
      </div>

      <div className="grid">
        <div data-span style={{ gridColumn: 'span 12' }}>
          <ChartFrame
            title="Version adoption"
            subtitle="Share of each platform's active users per build"
            table={{
              columns: ['Version', 'Platform', 'Client', 'Users', 'Share', 'Crash-free', 'Released'],
              rows: snap.versions.map((v) => [
                v.version,
                PLATFORM_LABELS[v.platform],
                PLATFORM_CLIENT[v.platform],
                full(v.users),
                percent(v.share),
                percent(v.crashFreeRate, 2),
                shortDate(v.releasedOn),
              ]),
            }}
          >
            <BarSeries
              data={adoptionData}
              categoryKey="name"
              series={active.map((p) => ({
                key: p,
                label: PLATFORM_LABELS[p],
                color: PLATFORM_COLORS[p],
              }))}
              layout="horizontal"
              stacked
              height={Math.max(260, adoptionData.length * 26)}
              categoryWidth={150}
              valueFormat={(n) => `${n.toFixed(0)}%`}
            />
          </ChartFrame>
        </div>

        {active.map((p) => (
          <div data-span style={{ gridColumn: active.length <= 2 ? 'span 6' : 'span 4' }} key={p}>
            <ChartFrame
              title={`${PLATFORM_LABELS[p]} builds`}
              subtitle={
                PLATFORM_CLIENT[p] === 'desktop' ? 'PyQt6 client — packaged' : 'Expo client — store'
              }
              table={{
                columns: ['Version', 'Users', 'Share', 'Crash-free', 'Released'],
                rows: byPlatform(p).map((v) => [
                  v.version,
                  full(v.users),
                  percent(v.share),
                  percent(v.crashFreeRate, 2),
                  shortDate(v.releasedOn),
                ]),
              }}
            >
              <VersionList rows={byPlatform(p)} color={PLATFORM_COLORS[p]} />
            </ChartFrame>
          </div>
        ))}
      </div>
    </>
  );
}
